import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTareas } from '../../context/TareasContext';
import { usePerfil } from '../../context/PerfilContext';
import { useAuth } from '../../context/AuthContext';
import { InvitacionCasa } from '../../types';
import { Logo } from '../../components/Logo';
import { Link2Off, ListChecks, LogOut } from 'lucide-react';

/**
 * Lo que ve quien abre un enlace de invitación a una casa. Como el de A medias,
 * va fuera de las guardas de cuenta activa: quien se acaba de registrar entra
 * por aquí. Tareas ya viene activado en las cuentas nuevas.
 */
export const UnirseCasa: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { perfil, cargandoPerfil, tieneModulo } = usePerfil();
  const { casas, leerInvitacion, unirseACasa, recargarCasas } = useTareas();

  const [invitacion, setInvitacion] = useState<InvitacionCasa | null | undefined>(undefined);
  const [uniendo, setUniendo] = useState(false);
  const [unido, setUnido] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setInvitacion(null); return; }
    leerInvitacion(token).then(setInvitacion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const yaDentro = !!invitacion && casas.some(c => c.id === invitacion.casaId);

  // Tras unirse se espera a que la casa llegue por el listener antes de entrar
  useEffect(() => {
    if (invitacion && (unido || yaDentro) && casas.some(c => c.id === invitacion.casaId)) {
      navigate('/tareas/casas', { replace: true });
    }
  }, [unido, yaDentro, casas, invitacion, navigate]);

  // Red de seguridad, como en A medias: se vuelve a pedir y, si no, se entra igual
  useEffect(() => {
    if (!unido || !invitacion) return;
    const t1 = setTimeout(recargarCasas, 2500);
    const t2 = setTimeout(recargarCasas, 5000);
    const t3 = setTimeout(() => navigate('/tareas/casas', { replace: true }), 8000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unido, invitacion]);

  const unirse = async () => {
    if (!invitacion) return;
    setUniendo(true);
    setError('');
    try {
      await unirseACasa(invitacion);
      setUnido(true);
    } catch (e) {
      console.error(e);
      setError('No se ha podido completar. Puede que el enlace acabe de cerrarse; pide uno nuevo.');
      setUniendo(false);
    }
  };

  const cargando = invitacion === undefined || cargandoPerfil || !perfil;
  // Se activa solo al entrar si nunca lo has tenido; si un administrador lo apagó, no
  const sinModulo = !cargando && !tieneModulo('tareas') && perfil?.modulos?.tareas === false;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 text-center">
        <div className="flex justify-center mb-6"><Logo size={48} /></div>

        {cargando ? (
          <p className="text-gray-500 dark:text-gray-400">Cargando…</p>
        ) : !invitacion || !invitacion.abierta ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
              <Link2Off className="w-7 h-7 text-gray-400" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Este enlace ya no vale</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Quien te lo pasó puede haberlo cerrado o cambiado por otro. Pídele uno nuevo.
            </p>
            <Link to="/" className="inline-block mt-5 text-sm font-semibold text-lumbre-700 dark:text-lumbre-400 hover:underline">
              Ir al inicio
            </Link>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-lumbre-50 dark:bg-lumbre-900/30 flex items-center justify-center text-4xl mb-4">
              {invitacion.iconoCasa}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {invitacion.creadaPorNombre || 'Alguien'} te invita a
            </p>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{invitacion.nombreCasa}</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
              Todos veréis las tareas de casa en una misma semana, sabréis a quién le toca cada una y podréis marcarlas
              al hacerlas.{' '}
              <Link to="/ayuda#tareas" className="font-semibold text-lumbre-700 dark:text-lumbre-400 hover:underline">
                ¿Cómo funciona?
              </Link>
            </p>

            {(error || sinModulo) && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                              text-red-700 dark:text-red-300 rounded-lg px-3 py-2 text-sm">
                {error || 'Tu cuenta no tiene Tareas activado. Pídeselo a un administrador.'}
              </div>
            )}

            <button onClick={unirse} disabled={uniendo || unido || yaDentro || sinModulo}
              className="w-full mt-6 inline-flex items-center justify-center gap-2 py-3 rounded-lg
                         bg-lumbre-600 hover:bg-lumbre-700 text-white font-semibold transition disabled:opacity-60">
              <ListChecks className="w-5 h-5" />
              {yaDentro ? 'Ya estás en esta casa' : uniendo || unido ? 'Entrando…' : 'Unirme a la casa'}
            </button>

            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
              Entras como {perfil?.nombre || user?.email}.{' '}
              <button onClick={logout} className="inline-flex items-center gap-0.5 font-semibold hover:underline">
                <LogOut className="w-3 h-3" /> No soy yo
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
};
